/**
 * One product page per Featured Categories primary slug so tile covers match
 * the first products on that category's Products browse page (newest first).
 */

import { useMemo } from "react";
import { useQuery } from "react-query";
import {
  fetchTemplateShopProductsRpcPage,
  type ShopCatalogProduct,
} from "../templateShopCatalog";

const COVER_LIMIT = 4;

export function useFeaturedCategoryCovers(args: {
  username: string;
  userId: string;
  enabled: boolean;
  slugs: string[];
}): {
  /** Up to 4 cover URLs per primary slug — same order as category browse. */
  coverUrlsBySlug: Map<string, string[]>;
  /** First cover per slug (facet-style map). */
  coverBySlug: Map<string, string>;
  products: ShopCatalogProduct[];
  loading: boolean;
} {
  const { username, userId, enabled } = args;
  const slugs = useMemo(() => {
    const out: string[] = [];
    const seen = new Set<string>();
    for (const raw of args.slugs) {
      const slug = String(raw || "").trim();
      if (!slug || seen.has(slug)) continue;
      seen.add(slug);
      out.push(slug);
    }
    return out.slice(0, 8);
  }, [args.slugs]);

  const slugsKey = slugs.join("+");
  const queryEnabled =
    enabled && Boolean(username && userId) && slugs.length > 0;

  const query = useQuery(
    ["featured-category-covers-v1", userId, username, slugsKey],
    async () => {
      const pages = await Promise.all(
        slugs.map((slug) =>
          fetchTemplateShopProductsRpcPage({
            username,
            userId,
            offset: 0,
            limit: COVER_LIMIT,
            primarySlug: slug,
            includeFacets: false,
          }),
        ),
      );
      const coverUrlsBySlug = new Map<string, string[]>();
      const coverBySlug = new Map<string, string>();
      const products: ShopCatalogProduct[] = [];
      const seenIds = new Set<string>();
      slugs.forEach((slug, i) => {
        const urls: string[] = [];
        const seenUrl = new Set<string>();
        for (const p of pages[i]?.products || []) {
          const id = String(p.postId || "").trim();
          if (id && !seenIds.has(id)) {
            seenIds.add(id);
            products.push(p);
          }
          const img = String(p.image || "").trim();
          if (!img || seenUrl.has(img)) continue;
          seenUrl.add(img);
          urls.push(img);
          if (urls.length >= COVER_LIMIT) break;
        }
        if (urls.length) {
          coverUrlsBySlug.set(slug, urls);
          coverBySlug.set(slug, urls[0]!);
        }
      });
      return { coverUrlsBySlug, coverBySlug, products };
    },
    {
      enabled: queryEnabled,
      staleTime: 60_000,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      // Keep prior covers while the new slug set fetches so category edits
      // don't skeleton the band / re-gate the Products catalog (page reload).
      keepPreviousData: true,
    },
  );

  // Initial load only — any prior cover payload keeps the band painted while
  // a new slug set fetches (keepPreviousData).
  const loading = Boolean(queryEnabled && !query.data);

  // While keepPreviousData is showing an older slug set, only keep covers for
  // slugs that are still selected — so a newly picked category falls through
  // to the browse catalog on the first click instead of waiting on the RPC.
  const coverUrlsBySlug = (() => {
    const raw = query.data?.coverUrlsBySlug ?? new Map<string, string[]>();
    if (!query.isPreviousData) return raw;
    const next = new Map<string, string[]>();
    for (const slug of slugs) {
      const urls = raw.get(slug);
      if (urls?.length) next.set(slug, urls);
    }
    return next;
  })();
  const coverBySlug = (() => {
    const raw = query.data?.coverBySlug ?? new Map<string, string>();
    if (!query.isPreviousData) return raw;
    const next = new Map<string, string>();
    for (const slug of slugs) {
      const img = raw.get(slug);
      if (img) next.set(slug, img);
    }
    return next;
  })();

  return {
    coverUrlsBySlug,
    coverBySlug,
    products: query.data?.products ?? [],
    loading,
  };
}
